import { Fragment, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  formatMoney,
  offerCategoryLabel,
  rankOffers,
  toCzkMinor,
  type CatalogItem,
  type Offer,
  type OfferCategory,
  type OffersResponse,
  type Store,
} from '@sp/shared'
import { useSignedIn } from '../session.tsx'
import { api } from '../lib/api.ts'
import { HttpError } from '../lib/http.ts'
import { useCatalog, useFx, useOfferMatches, useStores, useTemplates, useTrips } from '../lib/groupData.ts'
import { useDesktop } from '../lib/useDesktop.ts'
import { plural, relativeAgo, tripName } from '../lib/format.ts'
import {
  CATEGORY_ICONS,
  chainLabel,
  matchesByItem,
  offerOriginal,
  offerPack,
  offerPrice,
  offerTitle,
  offerValidity,
} from '../lib/offers.ts'
import { OfferImage } from '../components/OfferImage.tsx'
import { errorMessage } from '../lib/useResource.ts'
import { Icon } from '../components/Icon.tsx'
import {
  Button,
  Chip,
  EmptyState,
  Label,
  PageTitle,
  Paragraph,
  Segmented,
  SelectRow,
  Spinner,
  TextCol,
  TextField,
} from '../components/ui.tsx'
import { Inspector, Panel } from '../components/desk.tsx'
import { OfferBadge } from '../components/OfferBadge.tsx'
import { OfferSheet } from '../components/OfferSheet.tsx'
import { ChainPicker, chainsLabel } from '../components/ChainPicker.tsx'
import { Sheet } from '../components/Sheet.tsx'
import { ScrollRow } from '../components/ScrollRow.tsx'

/** Kolik akci se nacte najednou; dalsi po „Zobrazit další". */
const PAGE = 60

/** „před chvílí", „před 40 min", „před 3 h", jinak po dnech. */
function updatedAgo(iso: string, now = Date.now()): string {
  const minutes = Math.floor((now - Date.parse(iso)) / 60_000)
  if (minutes < 5) return 'před chvílí'
  if (minutes < 60) return `před ${minutes} min`
  if (minutes < 24 * 60) return `před ${Math.floor(minutes / 60)} h`
  return relativeAgo(iso)
}

/** Dnesni datum „2026-09-14" v mistnim case. */
function todayIso(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** „2026-09-19" -> Date o mistni pulnoci. */
function dayDate(iso: string): Date {
  const [y = 0, m = 1, d = 1] = iso.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** „so 19. 9." */
function shortDay(iso: string): string {
  const d = dayDate(iso)
  return `${d.toLocaleDateString('cs-CZ', { weekday: 'short' })} ${d.getDate()}. ${d.getMonth() + 1}.`
}

/** „ve 3 obchodech", „v 5 obchodech". */
function inStores(n: number): string {
  return `${n >= 2 && n <= 4 ? 've' : 'v'} ${plural(n, 'obchodě', 'obchodech', 'obchodech')}`
}

/**
 * Polozka sablony v akci vickrat: sbalena ukazuje obchody a nejnizsi cenu,
 * rozbalena akce od nejlevnejsi. Obchod ze sablony je jen oznaceny, nic neskryva.
 */
function OfferGroup({
  item,
  offers,
  open,
  templateStore,
  thumbs,
  czkOf,
  onToggle,
  onPick,
}: {
  item: CatalogItem
  /** Serazene od nejlevnejsi. */
  offers: readonly Offer[]
  open: boolean
  templateStore: Store | null
  thumbs: boolean
  czkOf(offer: Offer): number | null
  onToggle(): void
  onPick(offer: Offer): void
}) {
  const best = offers[0]
  if (!best) return null
  const chainIds = [...new Set(offers.map((offer) => offer.chain))]
  const onlyChain = chainIds.length === 1 ? chainIds[0] : undefined
  // „Nejlevněji" jen kdyz je co porovnat.
  const comparable = offers.filter((offer) => czkOf(offer) !== null).length >= 2
  const panelId = `offer-group-${item.id}`
  const summary = [
    onlyChain ? `${plural(offers.length, 'akce', 'akce', 'akcí')} · ${chainLabel(onlyChain)}` : `V akci ${inStores(chainIds.length)}`,
    templateStore ? `v šabloně ${templateStore.name}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className={`select-row offer-group${open ? ' is-open' : ''}`}>
      <button type="button" className="offer-group-head" aria-expanded={open} aria-controls={panelId} onClick={onToggle}>
        <TextCol
          title={item.name}
          sub={
            open ? (
              summary
            ) : (
              <span className="offer-sub">
                <span className="offer-chains">
                  {chainIds.map((id) => (
                    <span key={id} className="offer-chain">
                      {chainLabel(id)}
                    </span>
                  ))}
                </span>
                <OfferBadge offer={best} from />
              </span>
            )
          }
        />
        <span className="offer-group-count" aria-label={plural(offers.length, 'akce', 'akce', 'akcí')}>
          {offers.length}
        </span>
        <Icon name="chevron_down" size={20} className="offer-group-chev tone-muted" />
      </button>
      <div id={panelId} className="offer-group-body" aria-hidden={!open}>
        <div className="offer-group-clip">
          <div className="offer-group-list">
            {offers.map((offer, index) => {
              const price = offerPrice(offer)
              const old = offer.oldPriceMinor !== null ? formatMoney(offer.oldPriceMinor, offer.currency) : null
              const czk = offer.currency === 'CZK' ? null : czkOf(offer)
              const isBest = index === 0 && comparable && czkOf(offer) !== null
              const meta = [offerPack(offer), offer.brand, offerValidity(offer)].filter(Boolean).join(' · ')
              return (
                <button
                  key={offer.id}
                  type="button"
                  className={`offer-group-row${isBest ? ' is-best' : ''}`}
                  tabIndex={open ? 0 : -1}
                  onClick={() => onPick(offer)}
                >
                  {thumbs && <OfferImage offer={offer} size="thumb" />}
                  <span className="text-col">
                    <span className="offer-group-chain">
                      {chainLabel(offer.chain)}
                      {isBest && <span className="offer-tag is-best">nejlevněji</span>}
                      {templateStore?.offerChain === offer.chain && <span className="offer-tag">v šabloně</span>}
                    </span>
                    <span className="offer-group-name">{offerTitle(offer)}</span>
                    {meta && <span className="text-col-sub">{meta}</span>}
                  </span>
                  <span className="offer-row-price">
                    {price ? <strong>{price}</strong> : <span className="meta muted">bez ceny</span>}
                    {old && <span className="offer-old">{old}</span>}
                    {czk !== null && <span className="offer-czk">{`≈ ${Math.round(czk / 100)} Kč`}</span>}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

/** Akce z letaku obchodu skupiny: co ze sablony je v akci a vsechny akce s hledanim. */
export function OffersPage() {
  const s = useSignedIn()
  const groupId = s.group.id
  const navigate = useNavigate()
  const desktop = useDesktop()
  const catalog = useCatalog(groupId)
  const stores = useStores(groupId, true)
  const trips = useTrips(groupId)

  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  /** Vybrane retezce; prazdne = vsechny obchody skupiny. */
  const [picked, setPicked] = useState<string[]>([])
  /** Den rozpracovaneho nakupu, na ktery se akce filtruji; null = plati ted. */
  const [day, setDay] = useState<string | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [category, setCategory] = useState<OfferCategory | null>(null)
  const templates = useTemplates(groupId)
  /** Odkud jsou „Moje věci v akci": vse zbozi skupiny (''), jedna sablona, nebo nakup. */
  const [matchSource, setMatchSource] = useState('')
  const matches = useOfferMatches(groupId, day, matchSource || null)
  /** Telefon: moje veci v akci, nebo prohlizeni vsech letaku. */
  const [tab, setTab] = useState<'mine' | 'all'>('mine')
  const [data, setData] = useState<OffersResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<{ offer: Offer; item: CatalogItem | null } | null>(null)

  // Na nakup v dany den: jen retezce obchodu z trasy nakupu toho dne. Bezny nakup
  // „kdekoli" (bez obchodu) nic nezuzuje — muze se jet kamkoli.
  const dayChains = (() => {
    if (!day) return null
    const dayTrips = (trips.res.state === 'ok' ? trips.res.data : []).filter(
      (t) => t.status !== 'done' && t.plannedDate === day,
    )
    if (dayTrips.length === 0 || dayTrips.some((t) => t.storeIds.length === 0)) return null
    const groupStores = stores.res.state === 'ok' ? stores.res.data : []
    const ids = dayTrips.flatMap((t) => t.storeIds.flatMap((id) => groupStores.find((s) => s.id === id)?.offerChain ?? []))
    return [...new Set(ids)].sort()
  })()
  // Vybrany obchod, ktery na nakupu toho dne neni, se nepocita — jinak prazdny seznam.
  const pickedInDay = dayChains ? picked.filter((id) => dayChains.includes(id)) : picked
  const requestChains = pickedInDay.length > 0 ? pickedInDay : dayChains
  const requestKey = requestChains?.join(',') ?? null

  /** Vyber dne z filtru: moje veci pak ukazou nakup toho dne (drive efekt, ktery prebijel vybrany cip nakupu). */
  function pickDay(next: string | null) {
    setDay(next)
    const trip = next
      ? (trips.res.state === 'ok' ? trips.res.data : []).find((t) => t.status !== 'done' && t.plannedDate === next)
      : undefined
    setMatchSource((prev) => (trip ? `trip:${trip.id}` : prev.startsWith('trip:') ? '' : prev))
  }

  // Hledani az po chvilce psani, ne pri kazdem pismenu.
  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), 300)
    return () => clearTimeout(timer)
  }, [query])

  // Plati jen odpoved na posledni dotaz — starsi stranka nebo hledani dorazene
  // pozdeji by jinak prepsaly (nebo se pripojily k) novejsim vysledkum.
  const lastRequest = useRef(0)

  function load(offset: number) {
    const request = ++lastRequest.current
    const latest = () => request === lastRequest.current
    setLoading(true)
    setError(null)
    return api.offers
      .list(groupId, {
        ...(search ? { q: search } : {}),
        ...(day ? { date: day } : {}),
        ...(requestChains ? { chains: requestChains } : {}),
        ...(category ? { category } : {}),
        limit: PAGE,
        offset,
      })
      .then((res) => {
        if (latest()) setData((prev) => (offset > 0 && prev ? { ...res, offers: [...prev.offers, ...res.offers] } : res))
      })
      .catch((err: unknown) => {
        if (latest()) setError(err instanceof HttpError && err.isOffline ? 'Akce jde procházet jen se signálem.' : errorMessage(err))
      })
      .finally(() => {
        if (latest()) setLoading(false)
      })
  }

  useEffect(() => {
    void load(0)
    // load cte aktualni hledani, den a obchody; znovu jen pri jejich zmene.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, search, day, requestKey, category])

  const fx = useFx()
  const rates = fx.res.state === 'ok' ? fx.res.data : { eurCzk: null, plnCzk: null }
  /** Polozky sablony s vic akcemi, ktere jsou rozbalene. */
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())

  // Lista na pocitaci se po odjeti stranky zmensi — pozna se podle znacky nad ni.
  const barSentinel = useRef<HTMLDivElement>(null)
  const [stuck, setStuck] = useState(false)
  useEffect(() => {
    const el = barSentinel.current
    if (!el) return
    const observer = new IntersectionObserver(([entry]) => setStuck(entry ? !entry.isIntersecting : false), {
      rootMargin: '-12px 0px 0px 0px',
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [desktop])

  const items = catalog.res.state === 'ok' ? catalog.res.data : []
  const itemsById = new Map(items.map((item) => [item.id, item]))
  const storesById = new Map((stores.res.state === 'ok' ? stores.res.data : []).map((store) => [store.id, store]))
  // Akce ze vsech obchodu skupiny, od nejlevnejsi — podle nich se rozhoduje, kde koupit.
  const matched = [...matchesByItem(matches.res.state === 'ok' ? matches.res.data : [])]
    .flatMap(([id, found]) => {
      const item = itemsById.get(id)
      const ranked = rankOffers(dayChains ? found.filter((o) => dayChains.includes(o.chain)) : found, rates)
      return item && ranked[0] ? [{ item, offers: ranked, best: ranked[0] }] : []
    })
    .sort((a, b) => a.item.name.localeCompare(b.item.name, 'cs'))

  function toggleGroup(itemId: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (!next.delete(itemId)) next.add(itemId)
      return next
    })
  }

  function togglePicked(id: string) {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  function czkOf(offer: Offer): number | null {
    return offer.priceMinor === null ? null : toCzkMinor(offer.priceMinor, offer.currency, rates)
  }

  const groupChains = data?.chains ?? []
  const chains = dayChains ? groupChains.filter((id) => dayChains.includes(id)) : groupChains
  const offers = data?.offers ?? []
  const total = data?.total ?? 0
  const noChains = data !== null && groupChains.length === 0
  /** Rucni obnova letaku: bezi na pozadi, stranka se pak sama nacte znovu. */
  const [refreshing, setRefreshing] = useState(false)
  const [refreshNote, setRefreshNote] = useState<string | null>(null)
  const refreshAlive = useRef(true)
  useEffect(() => {
    // Nastavit i pri opakovanem pripojeni (StrictMode), jinak by cekani hned skoncilo.
    refreshAlive.current = true
    return () => {
      refreshAlive.current = false
    }
  }, [])

  async function refreshLeaflets() {
    setRefreshNote(null)
    try {
      const res = await api.offers.refresh(groupId)
      if (res.state === 'recent') {
        const at = res.nextAt ? new Date(res.nextAt).toLocaleTimeString('cs-CZ', { hour: 'numeric', minute: '2-digit' }) : null
        setRefreshNote(`Letáky se hledaly před chvílí${at ? `, znovu jde v ${at}` : ''}.`)
        return
      }
      setRefreshing(true)
      const before = data?.updatedAt ?? null
      // Sber trva od desitek sekund (Kaufland, Blix) po par minut (letaky ctene AI).
      for (let i = 0; i < 60 && refreshAlive.current; i++) {
        await new Promise((resolve) => setTimeout(resolve, i === 0 ? 5000 : 10_000))
        const status = await api.offers.refreshStatus(groupId)
        if (!status.running) {
          void load(0)
          matches.reload()
          setRefreshNote(
            // Kazdy povedeny sber posune updatedAt; beze zmeny se zadny zdroj nenacetl.
            status.updatedAt && status.updatedAt !== before
              ? 'Letáky jsou aktuální.'
              : 'Letáky se teď nepodařilo načíst, zkus to později.',
          )
          break
        }
      }
    } catch (err) {
      setRefreshNote(err instanceof HttpError && err.isOffline ? 'Letáky jde načíst jen se signálem.' : errorMessage(err))
    } finally {
      if (refreshAlive.current) setRefreshing(false)
    }
  }

  const refreshButton = !noChains && (
    <Button kind="text" compact icon="refresh" disabled={refreshing} onClick={() => void refreshLeaflets()}>
      {refreshing ? 'Hledám nové letáky…' : 'Hledat nové letáky'}
    </Button>
  )

  const subtitle = data?.updatedAt ? `Z letáků obchodů skupiny · aktualizováno ${updatedAgo(data.updatedAt)}` : 'Z letáků obchodů skupiny'

  const searchField = (className?: string) => (
    <TextField
      type="search"
      aria-label="Hledat v akcích"
      placeholder="Hledat, třeba máslo nebo čokoláda"
      value={query}
      onChange={(e) => setQuery(e.target.value)}
      className={className}
    />
  )

  // Dny rozpracovanych nakupu, na ktere se da akce zuzit.
  const today = todayIso()
  const tripDays = [
    ...new Set(
      (trips.res.state === 'ok' ? trips.res.data : []).flatMap((t) =>
        t.status !== 'done' && t.plannedDate && t.plannedDate >= today ? [t.plannedDate] : [],
      ),
    ),
  ].sort()
  const dayOptions = !noChains && tripDays.length > 0
  const chainOptions = !noChains && chains.length > 1
  const dayChips = (className?: string) => (
    <>
      <Chip className={className} active={day === null} onClick={() => pickDay(null)}>
        Platí teď
      </Chip>
      {tripDays.map((iso) => (
        <Chip key={iso} className={className} active={day === iso} onClick={() => pickDay(iso)}>
          {`Na nákup ${shortDay(iso)}`}
        </Chip>
      ))}
    </>
  )
  const activeFilters = (day ? 1 : 0) + (pickedInDay.length > 0 ? 1 : 0) + (category ? 1 : 0)
  // Pocty jsou pro stejne filtry bez kategorie. Vybrana kategorie bez akci zustane videt, at jde zrusit.
  const categoryCounts = data?.categories ?? []
  const categoryList =
    category && !categoryCounts.some((c) => c.id === category) ? [...categoryCounts, { id: category, count: 0 }] : categoryCounts
  const allCategoriesCount = categoryCounts.reduce((sum, c) => sum + c.count, 0)
  const categoryOptions = !noChains && categoryList.length > 1
  const toggleCategory = (id: OfferCategory) => setCategory((prev) => (prev === id ? null : id))
  const listTitle =
    [category ? offerCategoryLabel(category) : null, pickedInDay.length > 0 ? chainsLabel(pickedInDay) : null]
      .filter(Boolean)
      .join(' · ') || 'Všechny akce'

  // Obchody vydavaji letaky zhruba tyden dopredu — na pozdejsi den jeste nemusi byt.
  const beyondLeaflets =
    day !== null && data?.knownUntil != null && Date.parse(data.knownUntil) < dayDate(day).getTime()
  const leafletsHint = beyondLeaflets && day && (
    <Paragraph size="meta">
      {`Letáky na ${shortDay(day)} ještě nevyšly – obchody je vydávají zhruba týden dopředu. Zatím jsou vidět jen akce, které platí déle.`}
    </Paragraph>
  )

  const states = (
    <>
      {noChains && (
        <EmptyState title="Žádný obchod s letáky" action={<Button onClick={() => navigate('/skupina/obchody')}>Nastavit obchody</Button>}>
          U obchodu vyber řetězec – Kaufland, Biedronka, Netto… – a akce z jejich letáků se začnou načítat samy.
        </EmptyState>
      )}
      {error && <Paragraph tone="error">{error}</Paragraph>}
      {leafletsHint}
      {loading && offers.length === 0 && <Spinner />}
      {!loading && !noChains && !error && offers.length === 0 && (
        <Paragraph>
          {search
            ? 'Nic takového v akcích není.'
            : category
              ? 'V téhle kategorii teď žádné akce nejsou.'
              : day
              ? 'Na tenhle den zatím žádné akce nejsou.'
              : 'Akce se ještě nenačetly. Zkus to za chvíli.'}
        </Paragraph>
      )}
    </>
  )

  const hasMore = offers.length < total
  const sentinel = useRef<HTMLDivElement>(null)

  // Dalsi stranka se nacte sama, jakmile se konec seznamu priblizi k okraji obrazovky.
  useEffect(() => {
    const el = sentinel.current
    if (!el || !hasMore || loading || error) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void load(offers.length)
      },
      { rootMargin: '800px 0px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
    // load cte aktualni hledani a obchody; znovu jen pri zmene stranky nebo stavu nacitani.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, error, offers.length])

  const more = hasMore && (
    <div ref={sentinel} className="offers-more">
      {error ? (
        <Button kind="quiet" compact onClick={() => void load(offers.length)}>
          Načíst další akce
        </Button>
      ) : (
        loading && <Spinner />
      )}
    </div>
  )

  // Moje veci v akci: vse, jedna sablona, nebo rozpracovany nakup s datem (navrh v3 — cipy misto vyberu zdroje).
  const templateList = templates.res.state === 'ok' ? templates.res.data : []
  const allStores = stores.res.state === 'ok' ? stores.res.data : []
  const datedTrips = (trips.res.state === 'ok' ? trips.res.data : []).filter((t) => t.status !== 'done' && t.plannedDate)
  const sourceOptions = [
    { value: '', label: 'Vše' },
    ...templateList.map((t) => ({ value: `template:${t.id}`, label: t.name })),
    ...datedTrips.map((t) => ({ value: `trip:${t.id}`, label: `${tripName(t, allStores).title} ${shortDay(t.plannedDate ?? '')}` })),
  ]
  const activeSource = sourceOptions.some((o) => o.value === matchSource) ? matchSource : ''
  const sourceSelect = sourceOptions.length > 1 && (
    <ScrollRow className="chips-scroll" role="group" ariaLabel="Čí věci">
      {sourceOptions.map((o) => (
        <Chip
          key={o.value}
          className="chip-sm"
          active={o.value === activeSource}
          onClick={() => {
            setMatchSource(o.value)
            // Nakup ma svuj den; od nakupu zpet na sablonu plati akce ted.
            const trip = datedTrips.find((t) => `trip:${t.id}` === o.value)
            if (trip) setDay(trip.plannedDate)
            else if (activeSource.startsWith('trip:')) setDay(null)
          }}
        >
          {o.label}
        </Chip>
      ))}
    </ScrollRow>
  )
  const templateNames = new Map(templateList.map((t) => [t.id, t.name]))
  const templatesOfItem = new Map(
    (matches.res.state === 'ok' ? matches.res.data : []).map((m) => [
      m.catalogItemId,
      m.templateIds.flatMap((id) => templateNames.get(id) ?? []),
    ]),
  )
  // U jedne sablony by nazev sablony v radku jen opakoval vyber.
  const itemTemplates = (itemId: string) =>
    templateList.length > 1 && !matchSource.startsWith('template:') ? (templatesOfItem.get(itemId) ?? []).join(', ') : ''

  const matchRows =
    matched.length === 0 ? (
      <Paragraph size="meta">
        {matchSource === ''
          ? 'Z tvých věcí teď nic v akci není. U položky šablony můžeš doplnit, jak se jmenuje v letáku – třeba Butter nebo Masło.'
          : 'Z tohohle výběru teď nic v akci není.'}
      </Paragraph>
    ) : (
      matched.map(({ item, offers: found, best }) => {
        if (found.length === 1) {
          return (
            <SelectRow key={item.id} as="button" pad="even" onClick={() => setOpen({ offer: best, item })}>
              <TextCol
                title={item.name}
                sub={
                  <span className="offer-sub">
                    <span>{[itemTemplates(item.id), `${offerTitle(best)} · ${chainLabel(best.chain)}`].filter(Boolean).join(' · ')}</span>
                    <OfferBadge offer={best} />
                  </span>
                }
              />
              <Icon name="chevron_right" size={20} className="tone-muted" />
            </SelectRow>
          )
        }
        return (
          <OfferGroup
            key={item.id}
            item={item}
            offers={found}
            open={expanded.has(item.id)}
            templateStore={item.storeId ? (storesById.get(item.storeId) ?? null) : null}
            thumbs={!desktop}
            czkOf={czkOf}
            onToggle={() => toggleGroup(item.id)}
            onPick={(offer) => setOpen({ offer, item })}
          />
        )
      })
    )

  const sheet = (
    <OfferSheet
      offer={open?.offer ?? null}
      catalogItem={open?.item ?? null}
      onClose={() => setOpen(null)}
      addTo={{
        trips: trips.res.state === 'ok' ? trips.res.data : [],
        stores: stores.res.state === 'ok' ? stores.res.data : [],
      }}
      linkTo={{
        catalog: items,
        onLinked: (updated) => {
          catalog.setData((prev) => prev.map((i) => (i.id === updated.id ? updated : i)))
          matches.reload()
        },
      }}
    />
  )

  /** Pred prvni akci, ktera nema vsechna hledana slova — za ni jsou jen podobne. */
  function similarAt(index: number) {
    const exact = data?.exact ?? null
    if (exact === null || index !== exact || exact >= offers.length) return null
    return (
      <p className="offers-similar meta muted">
        {exact === 0 ? 'Přesně takhle nic v akci není – podobné akce' : 'Podobné akce'}
      </p>
    )
  }

  function oldPrice(offer: Offer): string | null {
    return offer.oldPriceMinor !== null ? formatMoney(offer.oldPriceMinor, offer.currency) : null
  }

  /** Na telefonu je v radku i nazev z letaku, na pocitaci ma v karte vlastni radek. */
  function offerSub(offer: Offer, withOriginal = false): string {
    return [
      withOriginal ? offerOriginal(offer) : null,
      // Karta na pocitaci ma baleni na vlastnim radku pod nazvem.
      withOriginal ? offerPack(offer) : null,
      offer.brand,
      chains.length > 1 && pickedInDay.length !== 1 ? chainLabel(offer.chain) : null,
      offerValidity(offer),
    ]
      .filter(Boolean)
      .join(' · ')
  }

  if (desktop) {
    return (
      <div className="desk-view">
        <div className="desk-view-main">
          <div ref={barSentinel} aria-hidden="true" />
          <header className={`offers-bar${stuck ? ' is-stuck' : ''}`}>
            <div className="offers-bar-row">
              <div className="offers-bar-text">
                <h1 className="desk-title">Akce</h1>
                <span className="desk-sub">{subtitle}</span>
                {refreshButton}
                {refreshNote && <span className="desk-sub">{refreshNote}</span>}
              </div>
              {!noChains && searchField('desk-search')}
            </div>
            {(dayOptions || chainOptions) && (
              <div className="offers-bar-row offers-bar-filters">
                {dayOptions && (
                  <div className="chips" role="group" aria-label="Kdy">
                    {dayChips('chip-sm')}
                  </div>
                )}
                {dayOptions && chainOptions && <span className="offers-bar-divider" aria-hidden="true" />}
                {chainOptions && <ChainPicker chains={chains} picked={pickedInDay} onChange={setPicked} />}
              </div>
            )}
            {categoryOptions && (
              <ScrollRow className="offers-bar-cats" role="group" ariaLabel="Kategorie">
                <Chip className="chip-sm" active={category === null} onClick={() => setCategory(null)}>
                  Vše <span className="chip-count">{allCategoriesCount}</span>
                </Chip>
                {categoryList.map((c) => (
                  <Chip key={c.id} className="chip-sm" active={category === c.id} onClick={() => toggleCategory(c.id)}>
                    <Icon name={CATEGORY_ICONS[c.id]} size={16} />
                    {offerCategoryLabel(c.id)} <span className="chip-count">{c.count}</span>
                  </Chip>
                ))}
              </ScrollRow>
            )}
          </header>
          {states}
          {offers.length > 0 && (
            <Panel title={listTitle} meta={String(total)}>
              <div className="offer-grid">
                {offers.map((offer, index) => {
                  const price = offerPrice(offer)
                  const old = oldPrice(offer)
                  return (
                    <Fragment key={offer.id}>
                      {similarAt(index)}
                      <button type="button" className="offer-card" onClick={() => setOpen({ offer, item: null })}>
                        <OfferImage offer={offer} size="card" />
                        <span className="meta muted">{offerSub(offer)}</span>
                        <span className="offer-card-name">{offerTitle(offer)}</span>
                        {offerOriginal(offer) && <span className="offer-card-orig">{offerOriginal(offer)}</span>}
                        {offerPack(offer) && <span className="offer-card-pack">{offerPack(offer)}</span>}
                        <span className="offer-card-foot">
                          <strong className="tabular">{price ?? 'cena v letáku'}</strong>
                          {old && <span className="offer-old">{old}</span>}
                        </span>
                      </button>
                    </Fragment>
                  )
                })}
              </div>
              {more}
            </Panel>
          )}
        </div>
        <Inspector label="Moje věci v akci">
          <h2 className="inspector-title">Moje věci v akci</h2>
          {sourceSelect}
          <div className="stack-8">{matchRows}</div>
        </Inspector>
        {sheet}
      </div>
    )
  }

  return (
    <>
      <PageTitle title="Akce" subtitle={subtitle} />
      {refreshButton && (
        <div className="row wrap-row">
          {refreshButton}
          {refreshNote && <Paragraph size="meta">{refreshNote}</Paragraph>}
        </div>
      )}
      {!noChains && (
        <Segmented
          label="Které akce"
          options={[
            { value: 'mine', label: matches.res.state === 'ok' ? `Moje věci · ${matched.length}` : 'Moje věci' },
            { value: 'all', label: 'Všechny letáky' },
          ]}
          value={tab}
          onChange={setTab}
        />
      )}
      {tab === 'mine' && !noChains && (
        <>
          {sourceSelect}
          <div className="stack-8">{matchRows}</div>
          {matched.length === 0 && (
            <Button className="self-start" icon="search" onClick={() => setTab('all')}>
              Procházet všechny letáky
            </Button>
          )}
        </>
      )}
      {tab === 'all' && !noChains && (
        <>
          <div className="offers-list-head">
            <Label>Všechny akce</Label>
            {data && <span className="meta muted tabular">{plural(total, 'akce', 'akce', 'akcí')}</span>}
          </div>
          {activeFilters > 0 && (
            <div className="chips wrap">
              {day && (
                <Chip className="chip-sm" active aria-label={`Zrušit filtr na nákup ${shortDay(day)}`} onClick={() => pickDay(null)}>
                  {`Na nákup ${shortDay(day)}`}
                  <Icon name="x" size={14} />
                </Chip>
              )}
              {pickedInDay.length > 0 && (
                <Chip className="chip-sm" active aria-label="Zrušit výběr obchodů" onClick={() => setPicked([])}>
                  {chainsLabel(pickedInDay)}
                  <Icon name="x" size={14} />
                </Chip>
              )}
              {category && (
                <Chip
                  className="chip-sm"
                  active
                  aria-label={`Zrušit kategorii ${offerCategoryLabel(category)}`}
                  onClick={() => setCategory(null)}
                >
                  <Icon name={CATEGORY_ICONS[category]} size={16} />
                  {offerCategoryLabel(category)}
                  <Icon name="x" size={14} />
                </Chip>
              )}
            </div>
          )}
        </>
      )}
      {(tab === 'all' || noChains) && states}
      {tab === 'all' && (
        <div className="stack-8">
          {offers.map((offer, index) => {
            const price = offerPrice(offer)
            const old = oldPrice(offer)
            return (
              <Fragment key={offer.id}>
                {similarAt(index)}
                <SelectRow as="button" pad="even" onClick={() => setOpen({ offer, item: null })}>
                  <OfferImage offer={offer} size="thumb" />
                  <TextCol title={offerTitle(offer)} sub={offerSub(offer, true)} />
                  <span className="offer-row-price">
                    {price ? <strong>{price}</strong> : <span className="meta muted">bez ceny</span>}
                    {old && <span className="offer-old">{old}</span>}
                  </span>
                </SelectRow>
              </Fragment>
            )
          })}
        </div>
      )}
      {tab === 'all' && more}
      {tab === 'all' && !noChains && (
        <>
          <div className="offers-dock-space" aria-hidden="true" />
          {/* Hledani a filtry dole nad listou, na dosah palce. */}
          <div className="offers-dock">
            {searchField('offers-dock-search')}
            {(dayOptions || chainOptions || categoryOptions) && (
              <button
                type="button"
                className="offers-dock-filter"
                aria-label={activeFilters > 0 ? `Filtry, zapnuté ${activeFilters}` : 'Filtry'}
                aria-haspopup="dialog"
                onClick={() => setFiltersOpen(true)}
              >
                <Icon name="filter" size={22} />
                {activeFilters > 0 && (
                  <span className="offers-dock-badge" aria-hidden="true">
                    {activeFilters}
                  </span>
                )}
              </button>
            )}
          </div>
        </>
      )}
      <Sheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title="Filtry"
        footer={
          <div className="grid-2">
            <Button
              kind="quiet"
              disabled={activeFilters === 0}
              onClick={() => {
                setDay(null)
                setPicked([])
                setCategory(null)
              }}
            >
              Zrušit vše
            </Button>
            <Button kind="primary" onClick={() => setFiltersOpen(false)}>
              {loading ? 'Načítám…' : `Zobrazit ${plural(total, 'akci', 'akce', 'akcí')}`}
            </Button>
          </div>
        }
      >
        <div className="stack">
          {dayOptions && (
            <div className="stack-8">
              <Label>Kdy</Label>
              <div className="chips wrap">{dayChips()}</div>
            </div>
          )}
          {categoryOptions && (
            <div className="stack-8">
              <Label>Kategorie</Label>
              <div className="cat-grid">
                {categoryList.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={`cat-tile${category === c.id ? ' is-active' : ''}`}
                    aria-pressed={category === c.id}
                    onClick={() => toggleCategory(c.id)}
                  >
                    <Icon name={CATEGORY_ICONS[c.id]} size={22} />
                    <span className="cat-tile-label">{offerCategoryLabel(c.id)}</span>
                    <span className="cat-tile-count">{c.count}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {chainOptions && (
            <div className="stack-8">
              <Label>Obchody</Label>
              <div className="chips wrap">
                <Chip active={pickedInDay.length === 0} onClick={() => setPicked([])}>
                  Všechny
                </Chip>
                {chains.map((id) => (
                  <Chip key={id} active={pickedInDay.includes(id)} onClick={() => togglePicked(id)}>
                    {chainLabel(id)}
                  </Chip>
                ))}
              </div>
            </div>
          )}
        </div>
      </Sheet>
      {sheet}
    </>
  )
}
